import { Alert, Button, CircularProgress, Dialog, DialogContent, Stack, TextField, Typography, useTheme } from '@mui/material'
import axios from 'axios';
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BASE_URL } from '../../config/config'

function ContactUs() {
    const theme = useTheme();
    const { t } = useTranslation();
    const intialFormState = {
        loading: false,
        successMsg: '',
        errorMsg: '',
        email: {
            value: '',
            errorMsg: ''
        },
        subject: {
            value: t('contact.defaultSubject'),
            errorMsg: ''
        },
        message: {
            value: '',
            errorMsg: ''
        }
    }
    const [contactFormData, setContactFormData] = useState(intialFormState)
    const [dialogOpen, setDialogOpen] = useState(false);

    // Update subject when language changes (only if it's the default value)
    useEffect(() => {
        const defaultSubjectEn = 'Astegne Website Enquiry';
        const currentSubject = contactFormData.subject.value;
        if (currentSubject === defaultSubjectEn || currentSubject === t('contact.defaultSubject')) {
            setContactFormData(prevState => ({
                ...prevState,
                subject: {
                    ...prevState.subject,
                    value: t('contact.defaultSubject')
                }
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    const validateForm = () => {
        let isValid = true;

        // Validate email
        if (!contactFormData.email.value) {
            setContactFormData(prevState => ({
                ...prevState,
                email: {
                    ...prevState.email,
                    errorMsg: t('contact.emailRequired')
                }
            }));
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(contactFormData.email.value)) {
            setContactFormData(prevState => ({
                ...prevState,
                email: {
                    ...prevState.email,
                    errorMsg: t('contact.invalidEmail')
                }
            }));
            isValid = false;
        }

        // Validate subject
        if (!contactFormData.subject.value) {
            setContactFormData(prevState => ({
                ...prevState,
                subject: {
                    ...prevState.subject,
                    errorMsg: t('contact.subjectRequired')
                }
            }));
            isValid = false;
        }

        // Validate message
        if (!contactFormData.message.value) {
            setContactFormData(prevState => ({
                ...prevState,
                message: {
                    ...prevState.message,
                    errorMsg: t('contact.messageRequired')
                }
            }));
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            // Submit the form data
            setContactFormData(prevState => ({ ...prevState, loading: true }));

            try {
                const response = await axios.post(
                    `${BASE_URL}/contact`,
                    {
                        email: contactFormData.email.value,
                        message: contactFormData.message.value,
                        subject: contactFormData.subject.value
                    });

                if (response.status === 200) {
                    setContactFormData(prevState => ({
                        ...prevState,
                        loading: false,
                        successMsg: response.data.message || t('contact.successMessage'),
                        errorMsg: ''
                    }));
                    setDialogOpen(true);
                } else {
                    setContactFormData(prevState => ({
                        ...prevState,
                        loading: false,
                        successMsg: '',
                        errorMsg: t('contact.errorMessage')
                    }));
                    setDialogOpen(true);
                }
            } catch (error) {
                setContactFormData(prevState => ({
                    ...prevState,
                    loading: false,
                    successMsg: '',
                    errorMsg: error.response?.data?.message || t('contact.errorMessage')
                }));
                setDialogOpen(true);
            }
        }
    };

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setContactFormData(prevState => ({
            ...prevState,
            [field]: {
                value,
                errorMsg: ''
            }
        }));
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setContactFormData(intialFormState);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack p={2} rowGap={2} justifyContent='center' alignItems={'center'}>
                <TextField
                    variant="standard"
                    size="medium"
                    sx={{
                        background: 'white',
                        borderRadius: '0.4em',
                        padding: '0.5em',
                        border: 'none',
                        width: { xs: '80vw', md: '30vw' },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                    placeholder={t('contact.email')}
                    value={contactFormData.email.value}
                    onChange={handleChange('email')}
                    error={!!contactFormData.email.errorMsg}
                    helperText={contactFormData.email.errorMsg}
                    InputProps={{
                        disableUnderline: true,
                    }}
                />
                <TextField
                    variant="standard"
                    size="medium"
                    minRows={5}
                    sx={{
                        background: 'white',
                        borderRadius: '0.4em',
                        padding: '0.5em',
                        border: 'none',
                        width: { xs: '80vw', md: '30vw' },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                    placeholder={t('contact.message')}
                    value={contactFormData.message.value}
                    onChange={handleChange('message')}
                    error={!!contactFormData.message.errorMsg}
                    helperText={contactFormData.message.errorMsg}
                    InputProps={{
                        disableUnderline: true,
                    }}
                />
                <Button
                    type='submit'
                    disabled={contactFormData.loading}
                    sx={{
                        background: theme.palette.secondary.main,
                        minWidth: '120px',
                        color: theme.palette.primary.main,
                        textTransform: 'capitalize',
                        "&:hover": {
                            background: theme.palette.secondary.dark,
                        }
                    }}
                    disableElevation
                    variant='contained'
                >
                    {contactFormData.loading ? (
                        <CircularProgress color='secondary' size={25} />
                    ) : (
                        <Typography fontWeight={600}>
                            {t('contact.sendMessage')}
                        </Typography>
                    )}
                </Button>
            </Stack>

            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogContent>
                    {contactFormData.successMsg && (
                        <Alert severity='success'>
                            {contactFormData.successMsg}
                        </Alert>
                    )}
                    {contactFormData.errorMsg && (
                        <Alert severity='error'>
                            {contactFormData.errorMsg}
                        </Alert>
                    )}
                </DialogContent>
            </Dialog>
        </form>
    );
}

export default ContactUs;
